import React, { useState, useEffect } from "react";
import {
  Tabs,
  Table,
  Tag,
  Card,
  Button,
  message,
  Space,
  Empty,
  Pagination,
  Modal,
} from "antd";
import { getDirectusClient } from "../../lib/directus";
import { readItems, updateItem } from "@directus/sdk";
import Layouts from "../../components/Layouts";
import { useSelector } from "react-redux";

import LiquidacionDetailsModal from "./components/LiquidacionDetailsModal";
import SearchFilter from "./components/SearchFilter";
import TotalsSummary from "./components/TotalsSummary";

const { TabPane } = Tabs;

export default function LiquidacionList() {
  const user = useSelector((state) => state.userdata);
  const [loading, setLoading] = useState(false);
  const [liquidaciones, setLiquidaciones] = useState({
    local: [],
    tropas: [],
    rutas: [],
  });

  const [selectedLiquidacion, setSelectedLiquidacion] = useState(null);
  const [detailsVisible, setDetailsVisible] = useState(false);
  const [selectedCajas, setSelectedCajas] = useState([]);

  // Individual state for each tab
  const [tabStates, setTabStates] = useState({
    local: {
      searchText: "",
      dateRange: null,
      aceptadaFilter: false,
      pagination: {
        current: 1,
        pageSize: 25,
        total: 0,
      },
      selectedRowKeys: [],
    },
    tropas: {
      searchText: "",
      dateRange: null,
      aceptadaFilter: false,
      pagination: {
        current: 1,
        pageSize: 25,
        total: 0,
      },
      selectedRowKeys: [],
    },
    rutas: {
      searchText: "",
      dateRange: null,
      aceptadaFilter: false,
      pagination: {
        current: 1,
        pageSize: 25,
        total: 0,
      },
      selectedRowKeys: [],
    },
  });



  const columns = [
    {
      title: "Responsable",
      dataIndex: ["responsable", "first_name"],
      key: "responsable",
      render: (text, record) =>
        `${record.responsable?.first_name || ""} ${
          record.responsable?.last_name || ""
        }`,
    },
    {
      title: "Cajas",
      dataIndex: "cajas",
      key: "cajas",
      render: (cajas) => cajas?.length || 0,
    },
    {
      title: "Fecha",
      dataIndex: "date_created",
      key: "date_created",
      render: (date) =>
        date
          ? new Date(date).toLocaleDateString("es-ES", {
              year: "numeric",
              month: "2-digit",
              day: "2-digit",
              hour: "2-digit",
              minute: "2-digit",
            })
          : "",
    },
    {
      title: "Estado",
      dataIndex: "estado",
      key: "estado",
      render: (estado) => {
        let color = "blue";
        if (estado === "incompleta") color = "orange";
        if (estado === "anulada") color = "red";
        if (estado === "creada") color = "green";

        return <Tag color={color}>{estado?.toUpperCase()}</Tag>;
      },
    },
    {
      title: "Acciones",
      key: "actions",
      render: (_, record) => (
        <Space>
          <Button onClick={() => handleViewDetails(record)}>
            Ver Detalles
          </Button>
          <Button
            type="primary"
            onClick={() => {
              Modal.confirm({
                title: "¿Está seguro?",
                content: "¿Desea aceptar esta liquidación?",
                okText: "Sí, aceptar",
                cancelText: "Cancelar",
                onOk: () => handleAceptarLiquidacion(record.id),
              });
            }}
            disabled={
              record.aceptada ||
              record.estado === "anulada" ||
              record.estado === "incompleta"
            }
          >
            Aceptar Liquidación
          </Button>
        </Space>
      ),
    },
  ];

  // Helper function to get row selection config for a specific tab
  const getRowSelection = (tipo) => ({
    selectedRowKeys: tabStates[tipo].selectedRowKeys,
    onChange: (selectedKeys) => {
      setTabStates((prev) => ({
        ...prev,
        [tipo]: {
          ...prev[tipo],
          selectedRowKeys: selectedKeys,
        },
      }));
    },
    getCheckboxProps: (record) => ({
      disabled: record.aceptada || record.estado === "anulada",
    }),
  });

  const handleAceptarLiquidacion = async (liquidacionId) => {
    try {
      const directus = await getDirectusClient();
      await directus.request(
        updateItem("liquidacion", liquidacionId, {
          aceptada: true,
          aceptada_por: user.id,
          fecha_aceptacion: new Date().toISOString(),
        })
      );
      message.success("Liquidación aceptada correctamente");
      // Refresh all tabs
      Object.keys(tabStates).forEach((tipo) => {
        fetchLiquidaciones(tipo);
      });
    } catch (error) {
      console.error("Error accepting liquidacion:", error);
      message.error("Error al aceptar la liquidación");
    }
  };

  // Nueva función para aceptar en lote solo las válidas
  const handleBulkAcceptConfirmed = async (ids, tipo) => {
    try {
      const directus = await getDirectusClient();
      const loadingMessage = message.loading("Aceptando liquidaciones...", 0);
      const promises = ids.map((id) =>
        directus.request(
          updateItem("liquidacion", id, {
            aceptada: true,
            aceptada_por: user.id,
            fecha_aceptacion: new Date().toISOString(),
          })
        )
      );
      await Promise.all(promises);
      setTabStates((prev) => ({
        ...prev,
        [tipo]: {
          ...prev[tipo],
          selectedRowKeys: [],
        },
      }));
      fetchLiquidaciones(tipo);
      loadingMessage();
      message.success(`${ids.length} liquidaciones aceptadas correctamente`);
    } catch (error) {
      console.error("Error accepting liquidaciones:", error);
      message.error("Error al aceptar las liquidaciones");
    }
  };

  // Modifica el handler de bulk accept
  const handleBulkAccept = async (tipo) => {
    const selectedLiquidaciones = liquidaciones[tipo].filter((l) =>
      tabStates[tipo].selectedRowKeys.includes(l.id)
    );

    const invalids = selectedLiquidaciones.filter(
      (l) => l.estado === "incompleta" || l.estado === "anulada"
    );
    const validIds = selectedLiquidaciones
      .filter((l) => l.estado !== "incompleta" && l.estado !== "anulada")
      .map((l) => l.id);

    if (invalids.length > 0) {
      Modal.warning({
        title: "Liquidaciones no válidas",
        content:
          "Existe una liquidación incompleta o anulada. Solo se aceptarán las demás.",
        onOk: () => {
          if (validIds.length > 0) {
            handleBulkAcceptConfirmed(validIds, tipo);
          }
        },
      });
    } else {
      handleBulkAcceptConfirmed(validIds, tipo);
    }
  };

  // Update fetchLiquidaciones function to work with specific tab
  const fetchLiquidaciones = async (tipo) => {
    setLoading(true);
    try {
      const directus = await getDirectusClient();
      const currentState = tabStates[tipo];

  
      let filter = {
        _and: [
          { aceptada: { _eq: currentState.aceptadaFilter } },
          { tipo: { _eq: tipo } }
        ]
      };

     
      if (
        currentState.dateRange &&
        currentState.dateRange[0] &&
        currentState.dateRange[1]
      ) {
        const startDate = currentState.dateRange[0].startOf('day');
        const endDate = currentState.dateRange[0].add(1, 'day').startOf('day');
        filter._and.push({
          date_created: {
            _between: [
              startDate.toISOString(),
              endDate.toISOString(),
            ],
          },
        });
      }

      // Get total count with filters
      const countResponse = await directus.request(
        readItems("liquidacion", {
          aggregate: {
            count: "id",
          },
          filter: filter,
        })
      );

      const totalItems = countResponse[0]?.count?.id || 0;

      // Get paginated data with date filter
      const response = await directus.request(
        readItems("liquidacion", {
          fields: [
            "id",
            "responsable.first_name",
            "responsable.last_name",
            "cajas.cajas_id",
            "estado",
            "tipo",
            "aceptada",
            "aceptada_por.first_name",
            "fecha_aceptacion",
            "total_facturas",
            "total_credito",
            "diferencia",
            "total_devoluciones",
            "novedades",
            "date_created",
          ],
          filter: filter,
          sort: ["-date_created"],
          limit: currentState.pagination.pageSize,
          offset:
            (currentState.pagination.current - 1) *
            currentState.pagination.pageSize,
        })
      );

      setLiquidaciones((prev) => ({
        ...prev,
        [tipo]: response,
      }));

      setTabStates((prev) => ({
        ...prev,
        [tipo]: {
          ...prev[tipo],
          pagination: {
            ...prev[tipo].pagination,
        total: totalItems,
          },
        },
      }));
    } catch (error) {
      console.error("Error fetching liquidaciones:", error);
      message.error("Error al cargar las liquidaciones");
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetails = async (liquidacion) => {
    setSelectedLiquidacion(liquidacion);
    setDetailsVisible(true);
    console.log(liquidacion);
    try {
      const directus = await getDirectusClient();
      const cajasIds = Array.isArray(liquidacion.cajas)
        ? liquidacion.cajas.map((c) => c.cajas_id)
        : [];
      const cajas = await directus.request(
        readItems("cajas", {
          fields: [
            "id",
            "vendedor.first_name",
            "vendedor.last_name",
            "estado",
            "liquidada",
            "fecha_apertura",
            "fecha_cierre",
          ],
          filter: {
            id: {
              _in: cajasIds,
            },
          },
        })
      );
      setSelectedCajas(cajas);
    } catch (error) {
      console.error("Error fetching cajas details:", error);
      message.error("Error al cargar los detalles de las cajas");
    }
  };

  // Individual handlers for each tab
  const handleSearch = (value, tipo) => {
    setTabStates((prev) => ({
      ...prev,
      [tipo]: {
        ...prev[tipo],
        searchText: value,
        pagination: {
          ...prev[tipo].pagination,
          current: 1,
        },
      },
    }));
    // Llamar fetchLiquidaciones después de actualizar el estado
  };

  const handleDateChange = (dates, tipo) => {
    setTabStates((prev) => ({
      ...prev,
      [tipo]: {
        ...prev[tipo],
        dateRange: dates,
        pagination: {
          ...prev[tipo].pagination,
          current: 1,
        },
      },
    }));

  };

  const handleAceptadaChange = (value, tipo) => {
    console.log("estado", value);
    
   
    const boolValue = value === true || value === 'true';
    setTabStates((prev) => ({
      ...prev,
      [tipo]: {
        ...prev[tipo],
        aceptadaFilter: boolValue,
        pagination: {
          ...prev[tipo].pagination,
          current: 1,
        },
      },
    }));
  
  };

  const handlePaginationChange = (page, pageSize, tipo) => {
    setTabStates((prev) => ({
      ...prev,
      [tipo]: {
        ...prev[tipo],
        pagination: {
          ...prev[tipo].pagination,
          current: page,
          pageSize: pageSize,
        },
      },
    }));
  
  };
 
  const renderTabContent = (tipo) => {
    const currentState = tabStates[tipo];

    return (
    <>
      <Space
        style={{
          marginBottom: 16,
          width: "100%",
          justifyContent: "space-between",
        }}
      >
        <SearchFilter
            onDateChange={(dates) => handleDateChange(dates, tipo)}
            onAceptadaChange={(value) => handleAceptadaChange(value, tipo)}
          loading={loading}
            aceptadaFilter={tabStates[tipo].aceptadaFilter}
        />
          {currentState.selectedRowKeys.length > 0 && (
            <Button
              type="primary"
              onClick={() => handleBulkAccept(tipo)}
              loading={loading}
            >
              Aceptar {currentState.selectedRowKeys.length} Liquidación(es)
          </Button>
        )}
      </Space>

      {/* Add TotalsSummary component */}
      <TotalsSummary liquidaciones={liquidaciones[tipo]} />

      <Table
        columns={columns}
        dataSource={liquidaciones[tipo]}
        loading={loading}
        rowKey="id"
          rowSelection={getRowSelection(tipo)}
        pagination={false}
        locale={{
          emptyText: (
            <Empty
              description={`No hay liquidaciones de ${tipo}`}
              image={Empty.PRESENTED_IMAGE_SIMPLE}
            />
          ),
        }}
      />
      <Pagination
          current={currentState.pagination.current}
          pageSize={currentState.pagination.pageSize}
          total={currentState.pagination.total}
        showSizeChanger={true}
        showQuickJumper={true}
        showTotal={(total) => `Total: ${total} registros`}
        pageSizeOptions={["10", "25", "50", "100"]}
          onChange={(page, pageSize) =>
            handlePaginationChange(page, pageSize, tipo)
          }
        style={{ marginTop: 16, textAlign: "right" }}
      />
    </>
  );
  };

  // Update initial useEffect to fetch all tabs
  useEffect(() => {
    Object.keys(tabStates).forEach((tipo) => {
      fetchLiquidaciones(tipo);
    });
    // eslint-disable-next-line
  }, [tabStates.local.aceptadaFilter, tabStates.tropas.aceptadaFilter, tabStates.rutas.aceptadaFilter,
    tabStates.local.dateRange, tabStates.tropas.dateRange, tabStates.rutas.dateRange,
    tabStates.local.pagination.current, tabStates.tropas.pagination.current, tabStates.rutas.pagination.current,
    tabStates.local.pagination.pageSize, tabStates.tropas.pagination.pageSize, tabStates.rutas.pagination.pageSize
]);


  return (
    <Layouts>
      <Card>
        <Tabs defaultActiveKey="local">
          <TabPane tab="Local" key="local">
            {renderTabContent("local")}
          </TabPane>
          <TabPane tab="Tropas" key="tropas">
            {renderTabContent("tropas")}
          </TabPane>
          <TabPane tab="Rutas" key="rutas">
            {renderTabContent("rutas")}
          </TabPane>
        </Tabs>
      </Card>

      <LiquidacionDetailsModal
        visible={detailsVisible}
        liquidacion={selectedLiquidacion}
        cajas={selectedCajas}
        onClose={() => {
          setDetailsVisible(false);
          setSelectedLiquidacion(null);
          setSelectedCajas([]);
        }}
      />
    </Layouts>
  );
}
